import CloseIcon from "@mui/icons-material/Close";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { CircularProgress, Dialog, IconButton } from "@mui/material";
import Button from "components/common/Button";
import GoogleAddress from "components/common/GoogleAddress";
import Input from "components/common/Input";
import api from "config/api";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { setUserData, toggleUserDetailModel } from "store/slices/main.slice";
import { useCachedImage } from "hooks/useCachedImage";
import { resolveUploadAssetUrl } from "utils/media";

const initialData = {
  clientId: 0,
  firstName: "",
  lastName: "",
  phoneNo: "",
  email: "",
  password: "",
  address: "",
  address1: "",
};

const normalizeCustomerResponse = (response: any, fallback: any) => {
  const payload = response?.data || response?.Data || response || {};
  return {
    ...initialData,
    ...fallback,
    ...payload,
    phoneNo: String(payload?.phoneNo ?? fallback?.phoneNo ?? "")
      .replace(/\D/g, "")
      .slice(0, 10),
    address: payload?.address ?? fallback?.address ?? "",
    address1: payload?.address1 ?? fallback?.address1 ?? "",
  };
};

const UserDetailPopUp = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [data, setData] = useState(initialData);
  const userData = useAppSelector(({ main }) => main.userData);
  const isOpen = useAppSelector(({ main }) => main.showUserDetailModel);
  const companyData = useAppSelector(({ main }) => main.companyData);
  const cachedPhoneNo = String(userData?.phoneNo || "").replace(/\D/g, "").slice(0, 10);
  const userDataRef = useRef(userData);
  const companyName = companyData?.companyName || "Your account";
  const resolvedLogoUrl = useMemo(
    () => resolveUploadAssetUrl(companyData?.logoUrl),
    [companyData?.logoUrl]
  );
  const logoUrl = useCachedImage(resolvedLogoUrl, { cacheKey: "company-logo" });

  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;
    const cachedUser = userDataRef.current || initialData;
    const phoneNo = cachedPhoneNo;

    setData({ ...initialData, phoneNo });

    if (!phoneNo) {
      setData({ ...initialData, ...cachedUser });
      return;
    }

    setFetchingProfile(true);
    api
      .get(`/api/Customer/ByPhone?phoneNo=${phoneNo}`)
      .then(({ data: responseData }) => {
        if (isCancelled) return;
        const freshUser = normalizeCustomerResponse(responseData, cachedUser);
        setData(freshUser);
        dispatch(setUserData(freshUser));
      })
      .catch(() => {
        if (isCancelled) return;
        setData({ ...initialData, ...cachedUser, phoneNo });
        toast.error("Unable to load latest customer details");
      })
      .finally(() => {
        if (!isCancelled) setFetchingProfile(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [cachedPhoneNo, dispatch, isOpen]);

  const closeModel = () => {
    dispatch(toggleUserDetailModel());
  };

  const handleSubmit = () => {
    toast.dismiss();

    if (!data.firstName || !data.lastName || !data.email) {
      toast.error("First name, last name and email are required");
      return;
    }

    if (data.phoneNo.length !== 10) {
      toast.error("Invalid mobile number");
      return;
    }

    setLoading(true);
    api
      .post("/api/Customer/UpdateCustomerData", data)
      .then(({ data: responseData }) => {
        const updatedUser = normalizeCustomerResponse(responseData, data);
        dispatch(setUserData(updatedUser));
        setData(updatedUser);
        toast.success("User details updated successfully");
        closeModel();
      })
      .catch(() => {
        toast.error("Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  return (
    <Dialog
      onClose={closeModel}
      open={isOpen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: "!rounded-2xl !shadow-2xl !overflow-visible",
      }}
    >
      <style>
        {`
          @keyframes profileAccentSlide {
            0% { transform: translateX(-42%); }
            50% { transform: translateX(42%); }
            100% { transform: translateX(-42%); }
          }
          @keyframes profileGlowPulse {
            0%, 100% { opacity: .42; }
            50% { opacity: .72; }
          }
          @keyframes profileGlassSweep {
            0% { transform: translateX(-115%) rotate(8deg); opacity: 0; }
            22% { opacity: .45; }
            55% { opacity: .28; }
            100% { transform: translateX(115%) rotate(8deg); opacity: 0; }
          }
        `}
      </style>
      <div className="relative bg-white p-4 text-[#111827] sm:p-6">
        <IconButton
          aria-label="Close customer details"
          onClick={closeModel}
          className="!absolute !right-3 !top-3 !z-10 !bg-white/95 !shadow-sm"
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <div className="relative mb-5 overflow-hidden rounded-2xl border border-white bg-white/75 p-4 pr-12 text-[#111827] shadow-[0_18px_45px_rgba(15,23,42,0.10)] ring-1 ring-black/5 backdrop-blur-xl sm:p-5">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.96),rgba(255,255,255,0.68)_44%,rgba(255,235,235,0.74))]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(255,0,0,0.14),transparent_28%),radial-gradient(circle_at_88%_18%,rgba(17,24,39,0.10),transparent_30%)]" />
          <div
            className="absolute inset-y-[-55%] left-0 w-1/2 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-sm"
            style={{ animation: "profileGlassSweep 5.2s ease-in-out infinite" }}
          />
          <div
            className="absolute inset-x-[-35%] top-0 h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent"
            style={{ animation: "profileAccentSlide 3.8s ease-in-out infinite" }}
          />
          <div
            className="absolute -right-12 -top-16 h-40 w-40 rounded-full bg-red-500/20 blur-3xl"
            style={{ animation: "profileGlowPulse 3s ease-in-out infinite" }}
          />
          <div className="relative flex items-center gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white shadow-md ring-1 ring-black/10">
              {logoUrl ? (
                <img
                  key={logoUrl}
                  src={logoUrl}
                  alt={companyName}
                  className="h-full w-full object-contain p-1.5"
                />
              ) : (
                <span className="text-[20px] font-semibold text-primary">
                  {companyName.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <p className="m-0 truncate text-[13px] font-semibold uppercase text-[#475467]">
                {companyName}
              </p>
              <h3 className="m-0 mt-1 text-[25px] font-semibold leading-tight text-[#101828] sm:text-[27px]">
                Your profile
              </h3>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/10 sm:p-5 [&_.MuiInputLabel-root]:!text-[13px] [&_.MuiInputLabel-root]:!font-normal [&_.MuiInputBase-input]:!text-[15px] [&_.MuiInputBase-input]:!font-normal [&_.MuiOutlinedInput-root]:!rounded-lg">
          {fetchingProfile ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center gap-3 text-[#667085]">
              <CircularProgress size={34} color="error" />
              <span className="text-[14px] font-medium">
                Loading latest customer details
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                size="small"
                type="text"
                label="First Name"
                value={data.firstName}
                onValueChange={(firstName) => setData((e) => ({ ...e, firstName }))}
              />
              <Input
                size="small"
                type="text"
                label="Last Name"
                value={data.lastName}
                onValueChange={(lastName) => setData((e) => ({ ...e, lastName }))}
              />
              <Input
                size="small"
                type="text"
                label="Phone No."
                value={data.phoneNo}
                onValueChange={(phoneNo) =>
                  setData((e) => ({
                    ...e,
                    phoneNo: String(phoneNo).replace(/\D/g, "").slice(0, 10),
                  }))
                }
              />
              <Input
                size="small"
                type="email"
                label="Email"
                value={data.email}
                onValueChange={(email) => setData((e) => ({ ...e, email }))}
              />

              <div className="sm:col-span-2 rounded-xl border border-[#eaecf0] bg-[#fcfcfd] p-3">
                <div className="mb-2 flex items-center gap-2 text-[14px] font-semibold text-[#344054]">
                  <HomeOutlinedIcon className="!text-[18px] text-primary" />
                  Home address
                </div>
                <GoogleAddress
                  value={data.address}
                  onChange={(address) => setData((e) => ({ ...e, address }))}
                  onAddressCommit={(address) => setData((e) => ({ ...e, address }))}
                />
              </div>

              <div className="sm:col-span-2 rounded-xl border border-[#eaecf0] bg-[#fcfcfd] p-3">
                <div className="mb-2 flex items-center gap-2 text-[14px] font-semibold text-[#344054]">
                  <WorkOutlineOutlinedIcon className="!text-[18px] text-primary" />
                  Office address
                </div>
                <GoogleAddress
                  value={data.address1}
                  onChange={(address1) => setData((e) => ({ ...e, address1 }))}
                  onAddressCommit={(address1) => setData((e) => ({ ...e, address1 }))}
                />
              </div>

              <div className="mt-1 flex flex-col-reverse gap-2 sm:col-span-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  className="!bg-white !text-[#344054] ring-1 ring-black/10"
                  onClick={closeModel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="min-w-[140px] !py-2"
                  disabled={loading}
                  onClick={handleSubmit}
                >
                  {loading ? "Updating..." : "Update profile"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default UserDetailPopUp;
